import React from 'react';
import { Box, Stack } from '@mui/material';
import { MetricCard } from '../../../ui-kit';
import { formatValue, toSparklinePoints } from '../format';
import type { KpiMetric } from '../../../types/manualDashboard';

function trendOf(metric: KpiMetric) {
  return metric.sparkline
    ? {
        points: toSparklinePoints(metric.sparkline),
        formatValue: (v: number | null) => formatValue(v, metric.format),
        label: `${metric.label} by cohort`,
      }
    : null;
}

/** Both of a tile's figures behind its one icon, the premium under its own heading. */
function tileHint(count: KpiMetric, premium: KpiMetric) {
  return (
    <>
      {count.definition}
      <Box component="span" sx={{ display: 'block', mt: 1.5, fontWeight: 600 }}>Premium</Box>
      {premium.definition}
    </>
  );
}

/**
 * Headline figures for the whole selected range, each over its own trend.
 *
 * No period-on-period change on this board: the sparkline under each figure
 * already shows how it moved, cohort by cohort, with unsettled cohorts
 * marked — a single "vs last month" figure would say less, and could compare
 * a settled cohort with one still settling.
 */
export default function KpiRow({ metrics, loading }: { metrics: KpiMetric[]; loading?: boolean }) {
  if (!metrics?.length) return null;
  const paired = metrics.filter((m) => m.premium);
  const single = metrics.filter((m) => !m.premium);
  // Tiles that carry a premium need the width, so they take a row of their
  // own, four across as on the Ops Management Report; the single figures share
  // the row under them.
  const rows = paired.length ? [paired, single].filter((r) => r.length) : [metrics];

  return (
    <Stack spacing={1.5}>
      {rows.map((row) => (
        <Box
          key={row[0].key}
          data-testid="kpi-tile-row"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(3, minmax(0, 1fr))',
              lg: `repeat(${row.length}, minmax(0, 1fr))`,
            },
            gap: 1.5,
          }}
        >
          {row.map((metric) => {
            const { premium } = metric;
            return (
              <MetricCard
                key={metric.key}
                label={metric.label}
                value={formatValue(metric.value, metric.format)}
                unit={premium ? 'cases' : undefined}
                info={premium ? tileHint(metric, premium) : metric.definition ?? undefined}
                loading={loading}
                trend={trendOf(metric)}
                // The premium on the same cases shares the tile: the same subject
                // in another unit, read together.
                secondary={premium ? {
                  value: formatValue(premium.value, premium.format),
                  label: 'premium',
                  trend: trendOf(premium),
                } : undefined}
              />
            );
          })}
        </Box>
      ))}
    </Stack>
  );
}
